import { defineStore } from "pinia";

import { relativeFromViewport } from "@/math/coordinate-utils";
import type { IdMap } from "@/model/baseTypes";
import type { BoardCard, Card } from "@/model/card";
import type { RelativeCoordinate, ViewportCoordinate } from "@/model/coordinates";

import { useBoardStore } from "./board";
import { useCardStore } from "./card";
import { linkId, useLinkStore } from "./link";

export interface Drag {
  id: string;
  type: "card" | "link";
  pos: RelativeCoordinate;
  grabFromOutside?: boolean;
  autoFocus?: boolean;
}

interface ExternalDragMetadata {
  grabFromOutside: boolean;
  autoFocus: boolean;
  initialPos: ViewportCoordinate;
}

const makeLinkId = (id: string) => `l_${id}`;

export const useDraggingStore = defineStore("dragging", {
  state: () => ({
    dragging: {} as IdMap<Drag>,
    externalDragMetadata: new Map<string, ExternalDragMetadata>(),
  }),
  getters: {
    findById:
      (state) =>
      (id: string): Drag | undefined => {
        for (const dragId in state.dragging) {
          if (state.dragging[dragId].id === id) {
            return state.dragging[dragId];
          }
        }
      },
    isLinkDragging: (state) => Object.values(state.dragging).some((drag) => drag.type === "link"),
    draggedLinks: (state) =>
      Object.values(state.dragging).flatMap((drag) => {
        const card = useBoardStore().currentBoard().cards[drag.id.substring(2)];
        if (!card || drag.type !== "link") {
          return [];
        }
        return { card, drag };
      }),
  },
  actions: {
    initCardDrag(card: Card): { grabFromOutside?: boolean } {
      // Check if this card has external drag metadata (from useDragToCreateSticky)
      const externalMetadata = this.getExternalDragMetadata(card.id);
      if (!externalMetadata) {
        // Normal drag from board - check if already in dragging store
        return this.findById(card.id) ?? {};
      }

      // Card was created externally and needs dragging state initialized
      // Initialize dragging state NOW (when drag gesture actually starts)
      const pos = relativeFromViewport(externalMetadata.initialPos);
      this.startCardDragging(card.id, pos, externalMetadata);
      this.clearExternalDragMetadata(card.id);
      return externalMetadata;
    },
    startCardDragging(
      id: string,
      pos: RelativeCoordinate,
      options: { grabFromOutside?: boolean; autoFocus?: boolean } = {},
    ) {
      const board = useBoardStore().currentBoard();
      const boardCard = board.cards[id];
      boardCard.meta.dragging = true;
      if (!(id in this.dragging)) {
        this.dragging[id] = {
          id,
          type: "card",
          grabFromOutside: !!options.grabFromOutside,
          autoFocus: !!options.autoFocus,
          pos,
        };
      }
    },
    startLinkDragging(id: string, dragId: number, pos: RelativeCoordinate) {
      const card = useCardStore().cards[id];
      this.dragging[dragId] = { id: makeLinkId(id), type: "link", pos };
      card.temporaryLinks.unshift({
        id: makeLinkId(id),
        from: linkId(card),
        to: "",
        state: "default",
      });
      useLinkStore().linking.from = card;
    },
    dragCard(id: string, pos: RelativeCoordinate) {
      this.dragging[id].pos = pos;
    },
    dragLink(dragId: number, pos: RelativeCoordinate) {
      this.dragging[dragId].pos = pos;
    },
    endCardDragging(id: string, pos: RelativeCoordinate) {
      delete this.dragging[id];
      const boardCard = useBoardStore().currentBoard().cards[id];
      if (!boardCard) {
        return;
      }
      boardCard.meta.pos.x = pos.x;
      boardCard.meta.pos.y = pos.y;
      boardCard.meta.zIndex = ++useBoardStore().currentBoard().maxZ;
      boardCard.meta.dragging = false;
    },
    endLinkDragging(
      id: string,
      dragId: number,
      boardCard: BoardCard,
      linkDragEl?: HTMLElement, // the element with .link-drag class
    ) {
      if (linkDragEl) {
        linkDragEl.style.display = "block";
      }

      const linkIndex = boardCard.data.temporaryLinks.findIndex((l) => l.id === makeLinkId(id));
      boardCard.data.temporaryLinks.splice(linkIndex, 1);
      delete this.dragging[dragId];
    },
    setExternalDragMetadata(cardId: string, metadata: ExternalDragMetadata) {
      this.externalDragMetadata.set(cardId, metadata);
    },
    getExternalDragMetadata(cardId: string): ExternalDragMetadata | undefined {
      return this.externalDragMetadata.get(cardId);
    },
    clearExternalDragMetadata(cardId: string) {
      this.externalDragMetadata.delete(cardId);
    },
  },
});
